/**
 * Settings vocabulary endpoints — AIMS backend.
 *
 *   GET/POST            /asset-categories/
 *   GET/PUT/PATCH/DELETE /asset-categories/{id}/
 *
 *   GET/POST            /asset-subcategories/?category={id}
 *   GET/PUT/PATCH/DELETE /asset-subcategories/{id}/
 *
 *   GET/POST            /asset-details/?subcategory={id}
 *   GET/PUT/PATCH/DELETE /asset-details/{id}/
 *
 *   GET/POST            /asset-outcomes/
 *   GET/PUT/PATCH/DELETE /asset-outcomes/{id}/
 *
 *   GET/POST            /asset-statuses/?contract_type=ADA|LIA
 *   GET/PUT/PATCH/DELETE /asset-statuses/{id}/
 */

import { aimsBackendApi } from "./api"
import {
  AssetCategoryCreate,
  AssetCategoryRecord,
  AssetDetailCreate,
  AssetDetailPatch,
  AssetDetailRecord,
  AssetOutcomeCreate,
  AssetOutcomeRecord,
  AssetStatusCreate,
  AssetStatusRecord,
  AssetSubcategoryCreate,
  AssetSubcategoryPatch,
  AssetSubcategoryRecord,
} from "./types"

export const aimsSettingsApi = aimsBackendApi.injectEndpoints({
  endpoints: builder => ({
    // ── Asset Categories ──────────────────────────────────────────
    listAssetCategories: builder.query<AssetCategoryRecord[], void>({
      query: () => "api/v1/aims/asset-categories/",
      providesTags: result => [
        { type: "AimsAssetCategory" as const, id: "LIST" },
        ...(result ?? []).map(c => ({ type: "AimsAssetCategory" as const, id: c.id })),
      ],
    }),
    createAssetCategory: builder.mutation<AssetCategoryRecord, AssetCategoryCreate>({
      query: body => ({ url: "api/v1/aims/asset-categories/", method: "POST", body }),
      invalidatesTags: [{ type: "AimsAssetCategory" as const, id: "LIST" }],
    }),
    updateAssetCategory: builder.mutation<AssetCategoryRecord, { id: number; body: AssetCategoryCreate }>({
      query: ({ id, body }) => ({ url: `api/v1/aims/asset-categories/${id}/`, method: "PUT", body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AimsAssetCategory" as const, id },
        { type: "AimsAssetCategory" as const, id: "LIST" },
        { type: "AimsAssetSubcategory" as const, id: "LIST" },
      ],
    }),
    patchAssetCategory: builder.mutation<AssetCategoryRecord, { id: number; body: Partial<AssetCategoryCreate> }>({
      query: ({ id, body }) => ({ url: `api/v1/aims/asset-categories/${id}/`, method: "PATCH", body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AimsAssetCategory" as const, id },
        { type: "AimsAssetCategory" as const, id: "LIST" },
      ],
    }),
    deleteAssetCategory: builder.mutation<void, number>({
      query: id => ({ url: `api/v1/aims/asset-categories/${id}/`, method: "DELETE" }),
      invalidatesTags: [
        { type: "AimsAssetCategory" as const, id: "LIST" },
        { type: "AimsAssetSubcategory" as const, id: "LIST" },
      ],
    }),

    // ── Asset Subcategories ───────────────────────────────────────
    listAssetSubcategories: builder.query<AssetSubcategoryRecord[], { category?: number } | void>({
      query: params => ({ url: "api/v1/aims/asset-subcategories/", params: params || undefined }),
      providesTags: result => [
        { type: "AimsAssetSubcategory" as const, id: "LIST" },
        ...(result ?? []).map(s => ({ type: "AimsAssetSubcategory" as const, id: s.id })),
      ],
    }),
    createAssetSubcategory: builder.mutation<AssetSubcategoryRecord, AssetSubcategoryCreate>({
      query: body => ({ url: "api/v1/aims/asset-subcategories/", method: "POST", body }),
      invalidatesTags: [{ type: "AimsAssetSubcategory" as const, id: "LIST" }],
    }),
    updateAssetSubcategory: builder.mutation<AssetSubcategoryRecord, { id: number; body: AssetSubcategoryCreate }>({
      query: ({ id, body }) => ({ url: `api/v1/aims/asset-subcategories/${id}/`, method: "PUT", body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AimsAssetSubcategory" as const, id },
        { type: "AimsAssetSubcategory" as const, id: "LIST" },
        { type: "AimsAssetDetail" as const, id: "LIST" },
      ],
    }),
    patchAssetSubcategory: builder.mutation<AssetSubcategoryRecord, { id: number; body: AssetSubcategoryPatch }>({
      query: ({ id, body }) => ({ url: `api/v1/aims/asset-subcategories/${id}/`, method: "PATCH", body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AimsAssetSubcategory" as const, id },
        { type: "AimsAssetSubcategory" as const, id: "LIST" },
      ],
    }),
    deleteAssetSubcategory: builder.mutation<void, number>({
      query: id => ({ url: `api/v1/aims/asset-subcategories/${id}/`, method: "DELETE" }),
      invalidatesTags: [
        { type: "AimsAssetSubcategory" as const, id: "LIST" },
        { type: "AimsAssetDetail" as const, id: "LIST" },
      ],
    }),

    // ── Asset Details ─────────────────────────────────────────────
    listAssetDetails: builder.query<AssetDetailRecord[], { subcategory?: number } | void>({
      query: params => ({ url: "api/v1/aims/asset-details/", params: params || undefined }),
      providesTags: result => [
        { type: "AimsAssetDetail" as const, id: "LIST" },
        ...(result ?? []).map(d => ({ type: "AimsAssetDetail" as const, id: d.id })),
      ],
    }),
    createAssetDetail: builder.mutation<AssetDetailRecord, AssetDetailCreate>({
      query: body => ({ url: "api/v1/aims/asset-details/", method: "POST", body }),
      invalidatesTags: [{ type: "AimsAssetDetail" as const, id: "LIST" }],
    }),
    updateAssetDetail: builder.mutation<AssetDetailRecord, { id: number; body: AssetDetailCreate }>({
      query: ({ id, body }) => ({ url: `api/v1/aims/asset-details/${id}/`, method: "PUT", body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AimsAssetDetail" as const, id },
        { type: "AimsAssetDetail" as const, id: "LIST" },
      ],
    }),
    patchAssetDetail: builder.mutation<AssetDetailRecord, { id: number; body: AssetDetailPatch }>({
      query: ({ id, body }) => ({ url: `api/v1/aims/asset-details/${id}/`, method: "PATCH", body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AimsAssetDetail" as const, id },
        { type: "AimsAssetDetail" as const, id: "LIST" },
      ],
    }),
    deleteAssetDetail: builder.mutation<void, number>({
      query: id => ({ url: `api/v1/aims/asset-details/${id}/`, method: "DELETE" }),
      invalidatesTags: [{ type: "AimsAssetDetail" as const, id: "LIST" }],
    }),

    // ── Asset Outcomes ────────────────────────────────────────────
    listAssetOutcomes: builder.query<AssetOutcomeRecord[], void>({
      query: () => "api/v1/aims/asset-outcomes/",
      providesTags: result => [
        { type: "AimsAssetOutcome" as const, id: "LIST" },
        ...(result ?? []).map(o => ({ type: "AimsAssetOutcome" as const, id: o.id })),
      ],
    }),
    createAssetOutcome: builder.mutation<AssetOutcomeRecord, AssetOutcomeCreate>({
      query: body => ({ url: "api/v1/aims/asset-outcomes/", method: "POST", body }),
      invalidatesTags: [{ type: "AimsAssetOutcome" as const, id: "LIST" }],
    }),
    updateAssetOutcome: builder.mutation<AssetOutcomeRecord, { id: number; body: AssetOutcomeCreate }>({
      query: ({ id, body }) => ({ url: `api/v1/aims/asset-outcomes/${id}/`, method: "PUT", body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AimsAssetOutcome" as const, id },
        { type: "AimsAssetOutcome" as const, id: "LIST" },
      ],
    }),
    patchAssetOutcome: builder.mutation<AssetOutcomeRecord, { id: number; body: Partial<AssetOutcomeCreate> }>({
      query: ({ id, body }) => ({ url: `api/v1/aims/asset-outcomes/${id}/`, method: "PATCH", body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AimsAssetOutcome" as const, id },
        { type: "AimsAssetOutcome" as const, id: "LIST" },
      ],
    }),
    deleteAssetOutcome: builder.mutation<void, number>({
      query: id => ({ url: `api/v1/aims/asset-outcomes/${id}/`, method: "DELETE" }),
      invalidatesTags: [{ type: "AimsAssetOutcome" as const, id: "LIST" }],
    }),

    // ── Asset Statuses ────────────────────────────────────────────
    listAssetStatuses: builder.query<
      AssetStatusRecord[],
      { contract_type?: "ADA" | "LIA"; round_number?: number } | void
    >({
      query: params => ({ url: "api/v1/aims/asset-statuses/", params: params || undefined }),
      providesTags: result => [
        { type: "AimsAssetStatus" as const, id: "LIST" },
        ...(result ?? []).map(s => ({ type: "AimsAssetStatus" as const, id: s.id })),
      ],
    }),
    createAssetStatus: builder.mutation<AssetStatusRecord, AssetStatusCreate>({
      query: body => ({ url: "api/v1/aims/asset-statuses/", method: "POST", body }),
      invalidatesTags: [{ type: "AimsAssetStatus" as const, id: "LIST" }],
    }),
    updateAssetStatus: builder.mutation<AssetStatusRecord, { id: number; body: AssetStatusCreate }>({
      query: ({ id, body }) => ({ url: `api/v1/aims/asset-statuses/${id}/`, method: "PUT", body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AimsAssetStatus" as const, id },
        { type: "AimsAssetStatus" as const, id: "LIST" },
      ],
    }),
    patchAssetStatus: builder.mutation<AssetStatusRecord, { id: number; body: Partial<AssetStatusCreate> }>({
      query: ({ id, body }) => ({ url: `api/v1/aims/asset-statuses/${id}/`, method: "PATCH", body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AimsAssetStatus" as const, id },
        { type: "AimsAssetStatus" as const, id: "LIST" },
      ],
    }),
    deleteAssetStatus: builder.mutation<void, number>({
      query: id => ({ url: `api/v1/aims/asset-statuses/${id}/`, method: "DELETE" }),
      invalidatesTags: [{ type: "AimsAssetStatus" as const, id: "LIST" }],
    }),
  }),
})

export const {
  // Categories
  useListAssetCategoriesQuery,
  useCreateAssetCategoryMutation,
  useUpdateAssetCategoryMutation,
  usePatchAssetCategoryMutation,
  useDeleteAssetCategoryMutation,
  // Subcategories
  useListAssetSubcategoriesQuery,
  useCreateAssetSubcategoryMutation,
  useUpdateAssetSubcategoryMutation,
  usePatchAssetSubcategoryMutation,
  useDeleteAssetSubcategoryMutation,
  // Details
  useListAssetDetailsQuery,
  useCreateAssetDetailMutation,
  useUpdateAssetDetailMutation,
  usePatchAssetDetailMutation,
  useDeleteAssetDetailMutation,
  // Outcomes
  useListAssetOutcomesQuery,
  useCreateAssetOutcomeMutation,
  useUpdateAssetOutcomeMutation,
  usePatchAssetOutcomeMutation,
  useDeleteAssetOutcomeMutation,
  // Statuses
  useListAssetStatusesQuery,
  useCreateAssetStatusMutation,
  useUpdateAssetStatusMutation,
  usePatchAssetStatusMutation,
  useDeleteAssetStatusMutation,
} = aimsSettingsApi
